import apiClient from './client';
import type {
  PlanningCycle,
  PlanningMetadata,
  IOPlanSummary,
  IOPlanStatus,
  LockableFeatureKey,
  FeatureLocksSummaryResponse,
  FeatureLockActionResponse,
} from '../types/planning';
import type { PlanLineItem } from '../types/io';
import { unwrapArray } from '../utils/api-helpers';
import {
  decodeBIComparisonData,
  decodeBIReferenceGLPhasingMeta,
  decodeCycleLineItemExportRow,
  decodeIOPlanSummary,
  decodeOptional,
  decodePlanLineItem,
  decodePlanningCycle,
} from '../utils/decodeApiStrings';
import { decodeHtml } from '../lib/planningCycleLabels';

// Cycles
export async function getPlanningCycles(params?: { fiscal_year?: number; cycle_type?: string }): Promise<PlanningCycle[]> {
  const { data } = await apiClient.get<PlanningCycle[]>('/planning-cycles', { params });
  return data.map(decodePlanningCycle);
}

export async function getActiveCycles(): Promise<PlanningCycle[]> {
  const { data } = await apiClient.get<PlanningCycle[]>('/planning-cycles/active');
  return data.map(decodePlanningCycle);
}

export async function getMetadata(): Promise<PlanningMetadata> {
  const { data } = await apiClient.get<PlanningMetadata>('/planning-cycles/metadata');
  return data;
}

export async function createCycle(cycle: Partial<PlanningCycle>): Promise<PlanningCycle> {
  const { data } = await apiClient.post<{ message?: string; cycle: PlanningCycle }>('/planning-cycles', cycle);
  const cycleData = (data as { cycle?: PlanningCycle }).cycle ?? (data as unknown as PlanningCycle);
  return decodePlanningCycle(cycleData);
}

export async function updateCycle(id: number, cycle: Partial<PlanningCycle>): Promise<PlanningCycle> {
  const { data } = await apiClient.put<{ message?: string; cycle: PlanningCycle }>(`/planning-cycles/${id}`, cycle);
  const cycleData = (data as { cycle?: PlanningCycle }).cycle ?? (data as unknown as PlanningCycle);
  return decodePlanningCycle(cycleData);
}

export async function reorderPlanningCycles(order: number[]): Promise<PlanningCycle[]> {
  const { data } = await apiClient.put<{ cycles?: PlanningCycle[] }>('/planning-cycles/reorder', { order });
  const cycles = Array.isArray((data as { cycles?: PlanningCycle[] }).cycles)
    ? (data as { cycles: PlanningCycle[] }).cycles
    : [];
  return cycles.map(decodePlanningCycle);
}

export async function deleteCycle(id: number, options?: { force?: boolean }): Promise<void> {
  const config = options?.force ? { params: { force: 'true' as const } } : {};
  await apiClient.delete(`/planning-cycles/${id}`, config);
}

export async function openCycle(id: number): Promise<void> {
  await apiClient.post(`/planning-cycles/${id}/open`);
}

export async function lockCycle(id: number): Promise<void> {
  await apiClient.post(`/planning-cycles/${id}/lock`);
}

export async function closeCycle(id: number): Promise<void> {
  await apiClient.post(`/planning-cycles/${id}/close`);
}

export async function reopenCycle(id: number): Promise<void> {
  await apiClient.post(`/planning-cycles/${id}/reopen`);
}

export async function getFeatureLocksSummary(cycleId: number): Promise<FeatureLocksSummaryResponse> {
  const { data } = await apiClient.get<FeatureLocksSummaryResponse>(`/planning-cycles/${cycleId}/feature-locks/summary`);
  return data;
}

export async function lockAllFeature(
  cycleId: number,
  feature: LockableFeatureKey,
): Promise<FeatureLockActionResponse> {
  const { data } = await apiClient.post<FeatureLockActionResponse>(
    `/planning-cycles/${cycleId}/feature-locks/${feature}/lock-all`,
  );
  return data;
}

export async function unlockAllFeature(
  cycleId: number,
  feature: LockableFeatureKey,
): Promise<FeatureLockActionResponse> {
  const { data } = await apiClient.post<FeatureLockActionResponse>(
    `/planning-cycles/${cycleId}/feature-locks/${feature}/unlock-all`,
  );
  return data;
}

// IO Summaries for a cycle
export async function getIOSummaries(
  cycleId: number,
  options?: { fresh?: boolean },
): Promise<IOPlanSummary[]> {
  const { data } = await apiClient.get(`/planning-cycles/${cycleId}/io-summaries`, {
    params: options?.fresh ? { fresh: '1' } : undefined,
  });
  return unwrapArray<IOPlanSummary>(data, 'summaries').map(decodeIOPlanSummary);
}

// Line Items (returns items + metadata)
export interface LineItemsResponse {
  line_items: PlanLineItem[];
  last_saved_at?: string;
  last_saved_by?: string;
}

export async function getLineItems(cycleId: number, ioId: number): Promise<PlanLineItem[]> {
  const { data } = await apiClient.get(`/planning-cycles/${cycleId}/ios/${ioId}/line-items`);
  const items: PlanLineItem[] = Array.isArray(data) ? data : (data?.line_items ?? []);
  return items.map(decodePlanLineItem);
}

export async function getLineItemsWithMeta(cycleId: number, ioId: number): Promise<LineItemsResponse> {
  const { data } = await apiClient.get(`/planning-cycles/${cycleId}/ios/${ioId}/line-items`);
  if (Array.isArray(data)) return { line_items: data.map(decodePlanLineItem) };
  return {
    line_items: (data?.line_items ?? []).map(decodePlanLineItem),
    last_saved_at: data?.last_saved_at,
    last_saved_by: data?.last_saved_by,
  };
}

/**
 * Bulk line-items fetch for the IO Plans detailed Excel export
 * (docs/IO_PLANS_EXPORT.md §6.1). Returns flattened rows (one per line item)
 * with the IO-level context the export needs, in a single round-trip so a
 * whole-cycle export doesn't fan out into one request per IO.
 *
 * Pass `ioIds` to scope the export to the IOs currently visible/filtered on
 * the page. The backend independently intersects this with the user's visible
 * set, so it can never widen access. By default IOs with no line items produce
 * no rows; pass `includeEmptyIos` (export only, PEAK-484) to also emit one
 * blank/$0 row per in-scope empty IO.
 */
export interface CycleLineItemExportRow {
  io_id: number;
  io_order: string | null;
  io_description: string | null;
  owner_name: string | null;
  team_name: string | null;
  detail_sheet_name: string | null;
  /** CTO/CFO reporting department (explicit per-IO, else app-department fallback). */
  reporting_department: string | null;
  io_type: 'Opex' | 'Capex' | null;
  spend_category: string | null;
  io_comment: string | null;
  /** Cost center code/name via the IO's cost center. Null if the IO has no cost center. */
  cost_center_code: string | null;
  cost_center_name: string | null;
  /** Finance legal-entity code/name the cost center rolls up to. Null if the
   *  cost center hasn't been reconciled to an entity yet (see
   *  backend/migrations/add_entities.py). */
  entity_code: string | null;
  entity_name: string | null;
  /** null for the synthetic FRE/SRE "Year-to-date actuals (unallocated)" row. */
  line_item_id: number | null;
  description: string | null;
  gl_code: string | null;
  gl_description: string | null;
  priority: string | null;
  budget_type: string | null;
  comments: string | null;
  /**
   * How Jan–Dec were derived for this row:
   *  - "BP plan (all months)"
   *  - "FRE/SRE projected landing (past months = actuals, planning months = plan)"
   * For FRE/SRE the backend overlays SAP actuals onto past months so the export
   * matches the IO Plan card. All amounts are ACTUAL DOLLARS (dollars-vs-thousands
   * is a pending Finance decision — see docs/IO_PLANS_EXPORT.md §6.5).
   */
  value_basis: string;
  jan: number; feb: number; mar: number; apr: number; may: number; jun: number;
  jul: number; aug: number; sep: number; oct: number; nov: number; dec: number;
  total: number;
}

export interface CycleLineItemsExportResponse {
  cycle: { id: number; label: string; cycle_type: string; fiscal_year: number };
  items: CycleLineItemExportRow[];
}

export async function getCycleLineItems(
  cycleId: number,
  ioIds?: number[],
  includeEmptyIos?: boolean,
): Promise<CycleLineItemsExportResponse> {
  const params: Record<string, string> = {};
  if (ioIds && ioIds.length) params.io_ids = ioIds.join(',');
  // PEAK-484: opt-in — also emit one blank/$0 row per in-scope IO with no line
  // items. Only the export requests this; the search-index caller omits it so
  // its payload is byte-identical to before.
  if (includeEmptyIos) params.include_empty_ios = 'true';
  const { data } = await apiClient.get<CycleLineItemsExportResponse>(
    `/planning-cycles/${cycleId}/line-items`,
    { params: Object.keys(params).length ? params : undefined },
  );
  return {
    ...data,
    items: (data.items ?? []).map((row) =>
      decodeCycleLineItemExportRow(row) as CycleLineItemExportRow,
    ),
  };
}

export async function createLineItem(cycleId: number, ioId: number, item: Partial<PlanLineItem>): Promise<PlanLineItem> {
  const { data } = await apiClient.post<PlanLineItem>(`/planning-cycles/${cycleId}/ios/${ioId}/line-items`, item);
  return decodePlanLineItem(data);
}

export async function updateLineItem(
  itemId: number,
  item: Partial<PlanLineItem>,
  options?: { triggerNotifications?: boolean },
): Promise<PlanLineItem> {
  const body = options?.triggerNotifications
    ? { ...item, trigger_notifications: true }
    : item;
  const { data } = await apiClient.put<PlanLineItem>(`/plan-line-items/${itemId}`, body);
  return decodePlanLineItem(data);
}

export async function deleteLineItem(itemId: number): Promise<void> {
  await apiClient.delete(`/plan-line-items/${itemId}`);
}

/**
 * PEAK-397: persist the Budget Owner's drag order for an IO's line items.
 * `itemIds` must be a full permutation of the IO's saved line items. Returns
 * the reordered list (with fresh `display_order`).
 */
export async function reorderLineItems(
  cycleId: number,
  ioId: number,
  itemIds: number[],
): Promise<PlanLineItem[]> {
  const { data } = await apiClient.put(
    `/planning-cycles/${cycleId}/ios/${ioId}/line-items/reorder`,
    { item_ids: itemIds },
  );
  const items: PlanLineItem[] = Array.isArray(data) ? data : (data?.line_items ?? []);
  return items.map(decodePlanLineItem);
}

// IO Comments
export interface IOPlanCommentResponse {
  comment: string | null;
  updated_at?: string | null;
  updated_by_name?: string | null;
}

export async function getIOComment(cycleId: number, ioId: number): Promise<IOPlanCommentResponse> {
  const { data } = await apiClient.get(`/planning-cycles/${cycleId}/ios/${ioId}/comment`);
  return {
    ...data,
    comment: decodeHtml(data.comment ?? ''),
  };
}

export interface IOPlanCommentSaveResponse {
  id: number;
  planning_cycle_id: number;
  io_id: number;
  comment: string | null;
  updated_by_id: number | null;
  updated_by_name: string | null;
  updated_at: string | null;
}

export async function updateIOComment(
  cycleId: number,
  ioId: number,
  comment: string,
  triggerNotifications = false,
): Promise<IOPlanCommentSaveResponse> {
  const { data } = await apiClient.put(`/planning-cycles/${cycleId}/ios/${ioId}/comment`, {
    comment,
    trigger_notifications: triggerNotifications,
  });
  return {
    ...data,
    comment: decodeOptional(data.comment) ?? null,
    updated_by_name: decodeOptional(data.updated_by_name) ?? null,
  };
}

// IO Sort Order
export async function getIOSortOrder(cycleId: number): Promise<{ io_ids: number[] }> {
  const { data } = await apiClient.get(`/planning-cycles/${cycleId}/io-sort-order`);
  return data;
}

export async function setIOSortOrder(cycleId: number, ioIds: number[]): Promise<void> {
  await apiClient.put(`/planning-cycles/${cycleId}/io-sort-order`, { io_ids: ioIds });
}

// BP comparison hidden orphan line items (per-user UI preference)
export async function getComparisonHiddenItems(
  cycleId: number,
  ioId: number,
): Promise<{ hidden_by_compare_cycle: Record<string, number[]> }> {
  const { data } = await apiClient.get(
    `/planning-cycles/${cycleId}/ios/${ioId}/comparison-hidden-items`,
  );
  return data;
}

export async function setComparisonHiddenItems(
  cycleId: number,
  ioId: number,
  hiddenByCompareCycle: Record<string, number[]>,
): Promise<{ hidden_by_compare_cycle: Record<string, number[]> }> {
  const { data } = await apiClient.put(
    `/planning-cycles/${cycleId}/ios/${ioId}/comparison-hidden-items`,
    { hidden_by_compare_cycle: hiddenByCompareCycle },
  );
  return data;
}

// Currencies
export interface CycleCurrency {
  id: number;
  planning_cycle_id: number;
  currency_code: string;
  currency_name: string;
  conversion_rate: number;
  created_at?: string;
  updated_at?: string;
}

export async function getCycleCurrencies(cycleId: number): Promise<CycleCurrency[]> {
  const { data } = await apiClient.get(`/planning-cycles/${cycleId}/currencies`);
  return data;
}

export async function addCycleCurrency(
  cycleId: number,
  body: { currency_code: string; currency_name: string; conversion_rate: number },
): Promise<CycleCurrency> {
  const { data } = await apiClient.post<{ currency?: CycleCurrency }>(
    `/planning-cycles/${cycleId}/currencies`,
    body,
  );
  return (data as { currency?: CycleCurrency }).currency ?? (data as unknown as CycleCurrency);
}

export async function updateCycleCurrency(
  cycleId: number,
  currencyId: number,
  body: { currency_name?: string; conversion_rate?: number },
): Promise<CycleCurrency> {
  const { data } = await apiClient.put<{ currency?: CycleCurrency }>(
    `/planning-cycles/${cycleId}/currencies/${currencyId}`,
    body,
  );
  return (data as { currency?: CycleCurrency }).currency ?? (data as unknown as CycleCurrency);
}

export async function deleteCycleCurrency(cycleId: number, currencyId: number): Promise<void> {
  await apiClient.delete(`/planning-cycles/${cycleId}/currencies/${currencyId}`);
}

// FRE/SRE Data
export interface FREData {
  planning_months: string[];
  /** The cycle being edited. `actuals_source` drives whether the card renders
   *  per-line-item detail-sheet actuals or transaction (invoice) actuals.
   *  See scripts/mvp-production-deployment/DETAIL_SHEET_ACTUALS.md. */
  cycle?: {
    id: number;
    cycle_type: string;
    fiscal_year: number;
    actuals_source?: 'transactions' | 'detail_sheet';
  };
  bp_line_items: PlanLineItem[];
  fre_line_items: PlanLineItem[];
  actuals_by_description: Record<string, Record<string, number>>;
  other_actuals: Record<string, number>;
  total_actuals: Record<string, number>;
  base_cycle: {
    id: number;
    cycle_type: string;
    fiscal_year: number;
    /** Months the base cycle actually planned. SRE uses this to label months outside
     *  FRE's planning range as "BP" (inherited) vs "FRE" (actively re-estimated). */
    planning_months?: string[] | null;
  };
}

export async function getFREData(cycleId: number, ioId: number): Promise<FREData> {
  const { data } = await apiClient.get(`/planning-cycles/${cycleId}/ios/${ioId}/fre-data`);
  return data;
}

export async function copyFromBase(cycleId: number, ioId: number): Promise<{ message: string; count: number }> {
  const { data } = await apiClient.post(`/planning-cycles/${cycleId}/ios/${ioId}/copy-from-base`);
  return data;
}

export interface ComparisonCycle {
  id: number;
  name: string;
  display_name: string;
  cycle_type: string;
  fiscal_year: number;
  /** Present only on prev_cycle when it's a FRE/SRE: months for which the prev plan applies. */
  planning_months?: string[] | null;
}

export interface BPComparisonData {
  prev_cycle: ComparisonCycle | null;
  prev_line_items: PlanLineItem[];
  available_comparisons: ComparisonCycle[];
  actuals_by_description?: Record<string, Record<string, number>>;
  other_actuals?: Record<string, number>;
  total_actuals?: Record<string, number>;
}

export interface BIRowMonthData {
  /** BP / FRE / ACT from the current cycle; COMP from a user-selected comparison cycle (display-only). */
  [dataType: string]: number;
}

export interface BIRow {
  gl_code: string;
  gl_description?: string;
  months: Record<number, BIRowMonthData>; // keyed by month number 1-12
}

export interface PlanComplianceEntry {
  month: number;
  bi_amount: number;
  app_amount: number;
  match: boolean;
  has_both: boolean;
  bi_only: boolean;
  app_only: boolean;
  /** True when Finance marked this GL as Phasing (FRE only). */
  phasing?: boolean;
  phasing_meta?: BIReferenceGLPhasingMeta;
}

/** Finance-admin Phasing override metadata (scoped to cycle + IO + GL + month). */
export interface BIReferenceGLPhasingMeta {
  id: number;
  planning_cycle_id: number;
  io_id: number;
  gl_code: string;
  /** Calendar month 1-12 the phasing applies to. */
  month: number;
  comment?: string | null;
  created_by_id: number;
  created_by_name?: string | null;
  updated_by_id?: number | null;
  updated_by_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Metadata for the user-selected comparison cycle's BI import (PEAK-436). */
export interface BIImportInfoComparison {
  cycle_id: number;
  cycle_type: string;
  fiscal_year: number;
  display_name: string;
  imported_at: string;
}

export interface BIImportInfo {
  current?: string;
  bp?: string;
  comparison?: BIImportInfoComparison;
}

export interface BIComparisonData {
  has_bi_data: boolean;
  /** True when compare_cycle_id supplied a comparison BI import (display-only COMP column). */
  has_comparison_bi?: boolean;
  /** True when the IO has plan line items, even if no BI has been imported yet. */
  has_plan_data?: boolean;
  import_id?: number;
  imported_at?: string;
  import_info?: BIImportInfo;
  io_order?: string;
  bi_rows: BIRow[];
  plan_compliance: Record<number, PlanComplianceEntry>;
  plan_compliance_by_gl: Record<string, PlanComplianceEntry & { gl_code: string; gl_description?: string | null }>;
  actuals_compliance: Record<number, { month: number; bi_amount: number; app_amount: number; variance: number; match: boolean }>;
  /** GL-level SAP actuals for past (non-planning) months, keyed `${gl_code}_${month}`. */
  actuals_compliance_by_gl?: Record<string, { gl_code: string; gl_description?: string | null; month: number; bi_amount: number; app_amount: number; variance: number; match: boolean; is_actuals_month?: boolean; has_both?: boolean; bi_only?: boolean; app_only?: boolean }>;
  /** Per-month sum ($K) of past-month actuals with no GL mapping (fallback row). */
  actuals_unmapped_by_month?: Record<number, number>;
  overall_plan_compliant: boolean;
  overall_actuals_compliant: boolean;
  plan_match_count: number;
  plan_total_count: number;
  actuals_match_count: number;
  actuals_total_count: number;
  /** True when past-month SAP actuals exist that are not attributed to any line item. */
  has_untagged_actuals?: boolean;
  /** Sum of untagged actuals in $K. */
  untagged_actuals_k?: number;
  /** True when at least one cell on this IO is marked Phasing. */
  has_phasing?: boolean;
  /** Phasing overrides keyed by `${uppercased GL code}_${month}` (matches plan_compliance_by_gl). */
  phasing_by_gl_month?: Record<string, BIReferenceGLPhasingMeta>;
  message?: string;
}

export async function getBIComparison(
  cycleId: number,
  ioId: number,
  compareCycleId?: number,
): Promise<BIComparisonData> {
  const params = compareCycleId ? { compare_cycle_id: compareCycleId } : {};
  const { data } = await apiClient.get(`/bi-comparison/cycles/${cycleId}/ios/${ioId}/comparison`, { params });
  return decodeBIComparisonData(data);
}

/** Finance-admin: mark or update Phasing on a single BI Reference cell (GL + month, FRE cycles only). */
export async function setBIReferenceGLPhasing(
  cycleId: number,
  ioId: number,
  glCode: string,
  month: number,
  comment?: string | null,
): Promise<{ success: boolean; phasing: BIReferenceGLPhasingMeta }> {
  const { data } = await apiClient.post(
    `/bi-comparison/cycles/${cycleId}/ios/${ioId}/phasing`,
    { gl_code: glCode, month, comment: comment ?? null },
  );
  return {
    ...data,
    phasing: decodeBIReferenceGLPhasingMeta(data.phasing),
  };
}

/** Finance-admin: remove Phasing from a single BI Reference cell (GL + month, FRE cycles only). */
export async function removeBIReferenceGLPhasing(
  cycleId: number,
  ioId: number,
  glCode: string,
  month: number,
): Promise<{ success: boolean }> {
  const { data } = await apiClient.delete(
    `/bi-comparison/cycles/${cycleId}/ios/${ioId}/phasing/${encodeURIComponent(glCode)}/${month}`,
  );
  return data;
}

/** Pre-calculated BI compliance for badge / FI summary (amounts in $K). */
export interface BISummaryData {
  has_bi_data: boolean;
  total_variance: number;
  overall_plan_compliant: boolean;
  total_bi_amount: number;
  total_app_amount: number;
  plan_match_count: number;
  plan_total_count: number;
}

export async function getBISummary(cycleId: number, ioId: number): Promise<BISummaryData> {
  const { data } = await apiClient.get(`/bi-comparison/cycles/${cycleId}/ios/${ioId}/summary`);
  return data;
}

export async function getBPComparison(cycleId: number, ioId: number, compareCycleId?: number): Promise<BPComparisonData> {
  const params = compareCycleId ? { compare_cycle_id: compareCycleId } : {};
  const { data } = await apiClient.get(`/planning-cycles/${cycleId}/ios/${ioId}/bp-comparison`, { params });
  return data;
}

// Warnings acknowledgement (a.k.a. "No budget planned" — marks IO inactive, pauses notifications)
export async function setIOWarningsAck(
  cycleId: number,
  ioId: number,
  acknowledged: boolean,
): Promise<{ success: boolean; acknowledged: boolean; io_id: number; cycle_id: number }> {
  const { data } = await apiClient.put(
    `/planning-cycles/${cycleId}/ios/${ioId}/warnings-ack`,
    { acknowledged },
  );
  return data;
}

// PEAK-96: Per-IO submission and lock state -------------------------------

/**
 * Budget Owner action: mark an IO as submitted within the active planning cycle.
 * Owner can still edit and re-submit until an Admin locks the IO.
 */
export async function submitIOPlan(cycleId: number, ioId: number): Promise<IOPlanStatus> {
  const { data } = await apiClient.post<IOPlanStatus>(
    `/planning-cycles/${cycleId}/ios/${ioId}/submit`,
  );
  return data;
}

/**
 * PEAK-96: pre-flight read of the per-IO submit/lock state. Bypasses the
 * `/io-summaries` cache so the UI sees the truth even after another user
 * (e.g., the owner) has just edited or re-submitted in another session.
 */
export async function getIOPlanStatus(cycleId: number, ioId: number): Promise<IOPlanStatus> {
  const { data } = await apiClient.get<IOPlanStatus>(
    `/planning-cycles/${cycleId}/ios/${ioId}/status`,
  );
  return data;
}

/**
 * Admin action: lock a submitted IO so its budget data cannot be altered
 * during Finance review. Requires the IO to have been submitted first.
 */
export async function lockIOPlan(cycleId: number, ioId: number): Promise<IOPlanStatus> {
  const { data } = await apiClient.post<IOPlanStatus>(
    `/planning-cycles/${cycleId}/ios/${ioId}/lock`,
  );
  return data;
}

/**
 * Admin action: unlock a previously-locked IO. Clears BOTH submission and
 * lock flags so the Budget Owner can resume editing and submit again.
 */
export async function unlockIOPlan(cycleId: number, ioId: number): Promise<IOPlanStatus> {
  const { data } = await apiClient.post<IOPlanStatus>(
    `/planning-cycles/${cycleId}/ios/${ioId}/unlock`,
  );
  return data;
}
